import type {
  OverlayDoc,
  OverlayGradient,
  OverlayTextItem,
} from "~/ui-rtk/api/adForgeApi"
import {
  solveOverlay,
  type SolvedLayout,
  type TextMeasurer,
} from "./overlay-solver"

export { fontFamiliesInDoc } from "./overlay-solver"

/**
 * Canvas rasterizer for the overlay document: solve the flow layout (shared
 * with the DOM editor), then paint bands and items. Because the editor
 * renders the SAME solved boxes, canvas output matches the editing view.
 */

export interface RenderAssets {
  /** Brand logo, already loaded (and taint-free for export). */
  logo?: HTMLImageElement | null
}

export const fontStack = (family: string): string =>
  `"${family}", ui-sans-serif, system-ui, sans-serif`

const GENERIC_FAMILIES = new Set([
  "sans-serif",
  "serif",
  "monospace",
  "system-ui",
  "ui-sans-serif",
  "inter", // bundled app font — no Google Fonts fetch needed
])

// family → promise that resolves once its Google Fonts STYLESHEET is loaded
// (only then does document.fonts know the faces exist). NOTE: no
// `document.fonts.check()` pre-check here — check() returns TRUE for
// families with no registered @font-face at all (vacuous match), which
// silently skipped injection and left every brand font on the fallback.
const stylesheetByFamily = new Map<string, Promise<void>>()

function injectFamilyStylesheet(family: string): Promise<void> {
  const existing = stylesheetByFamily.get(family)
  if (existing) return existing
  const promise = new Promise<void>(resolve => {
    const link = document.createElement("link")
    link.rel = "stylesheet"
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(
      family,
    ).replace(/%20/g, "+")}:wght@300;400;500;600;700;800;900&display=swap`
    link.onload = () => resolve()
    link.onerror = () => resolve() // unknown family → fallback stack, don't block
    document.head.appendChild(link)
    // Belt-and-braces: never wedge rendering on a slow fonts CDN.
    setTimeout(resolve, 4000)
  })
  stylesheetByFamily.set(family, promise)
  return promise
}

// Company-owned fonts registered via the FontFace API (Brand Kit uploads).
// family → promise resolving once the face is added to document.fonts. These
// families are NEVER routed to Google Fonts.
const customFontByFamily = new Map<string, Promise<void>>()

/**
 * Register an uploaded brand font from a data: URL (fonts are CORS-gated, so
 * the raw S3 URL can't be fetched — callers proxy it first). Declared with
 * the full weight range so one file serves every weight the canvas asks for.
 */
export function registerCustomFont(
  family: string,
  dataUrl: string,
): Promise<void> {
  const existing = customFontByFamily.get(family)
  if (existing) return existing
  const promise = (async () => {
    const face = new FontFace(family, `url(${dataUrl})`, {
      weight: "100 900",
    })
    await face.load()
    document.fonts.add(face)
  })().catch(() => {
    // Corrupt/unsupported file → fallback stack; never block rendering.
  })
  customFontByFamily.set(family, promise)
  return promise
}

export const isCustomFontFamily = (family: string): boolean =>
  customFontByFamily.has(family)

/**
 * Best-effort web-font loading for brand fonts: inject a Google Fonts
 * stylesheet per family, WAIT for it (the FontFaceSet only learns the faces
 * once the CSS is parsed — calling document.fonts.load() earlier matches
 * nothing and resolves instantly with the fallback still active), then load
 * the faces. Registered custom (uploaded) families await their FontFace
 * instead. Unknown families silently fall back to the font stack.
 */
export async function ensureFontsLoaded(families: string[]): Promise<void> {
  const requested = [...new Set(families.filter(Boolean))].filter(
    family => !GENERIC_FAMILIES.has(family.toLowerCase()),
  )
  const custom = requested.filter(family => customFontByFamily.has(family))
  const wanted = requested.filter(family => !customFontByFamily.has(family))
  await Promise.all(custom.map(family => customFontByFamily.get(family)))
  if (wanted.length === 0) return

  await Promise.all(wanted.map(injectFamilyStylesheet))
  try {
    await Promise.all(
      wanted.flatMap(family => [
        document.fonts.load(`700 16px "${family}"`),
        document.fonts.load(`400 16px "${family}"`),
        document.fonts.load(`900 16px "${family}"`),
      ]),
    )
  } catch {
    // Font loading is best-effort; fall back to the stack.
  }
}

/** Set font + letter spacing together — both affect measureText. */
function applyTextStyle(
  ctx: CanvasRenderingContext2D,
  item: OverlayTextItem,
  scale: number,
): void {
  ctx.font = `${item.fontWeight} ${item.fontSize * scale}px ${fontStack(item.fontFamily)}`
  // letterSpacing is missing from older TS dom libs — narrow cast.
  const ctxWithSpacing = ctx as CanvasRenderingContext2D & {
    letterSpacing?: string
  }
  if ("letterSpacing" in ctx) {
    ctxWithSpacing.letterSpacing = `${(item.letterSpacing ?? 0) * scale}px`
  }
}

/** Word-wrap `text` into lines that fit `maxWidth` with the current ctx font. */
export function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
): string[] {
  const lines: string[] = []
  for (const rawLine of text.split("\n")) {
    const words = rawLine.split(/\s+/).filter(Boolean)
    if (words.length === 0) {
      lines.push("")
      continue
    }
    let line = ""
    for (const word of words) {
      const candidate = line ? `${line} ${word}` : word
      if (line && ctx.measureText(candidate).width > maxWidth) {
        lines.push(line)
        line = word
      } else {
        line = candidate
      }
    }
    lines.push(line)
  }
  return lines
}

let sharedMeasureCtx: CanvasRenderingContext2D | null = null

/**
 * Doc-space text measurer for the solver (font at scale 1). The editor and
 * exporter share it so wraps are identical everywhere.
 */
export function createTextMeasurer(): TextMeasurer {
  if (!sharedMeasureCtx) {
    sharedMeasureCtx = document.createElement("canvas").getContext("2d")
  }
  const ctx = sharedMeasureCtx
  return (item, maxWidth) => {
    if (!ctx) {
      // Degenerate fallback: single line, rough width.
      const content = item.uppercase ? item.text.toUpperCase() : item.text
      return {
        lines: [content],
        blockWidth: content.length * item.fontSize * 0.55,
      }
    }
    applyTextStyle(ctx, item, 1)
    const content = item.uppercase ? item.text.toUpperCase() : item.text
    const lines = wrapText(ctx, content, maxWidth)
    const blockWidth = Math.min(
      maxWidth,
      Math.max(...lines.map(line => ctx.measureText(line).width), 1),
    )
    return { lines, blockWidth }
  }
}

export const solveDoc = (doc: OverlayDoc): SolvedLayout =>
  solveOverlay(doc, createTextMeasurer())

function roundedRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  radius: number,
): void {
  const r = Math.min(radius, w / 2, h / 2)
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.arcTo(x + w, y, x + w, y + h, r)
  ctx.arcTo(x + w, y + h, x, y + h, r)
  ctx.arcTo(x, y + h, x, y, r)
  ctx.arcTo(x, y, x + w, y, r)
  ctx.closePath()
}

function gradientFillStyle(
  ctx: CanvasRenderingContext2D,
  gradient: OverlayGradient,
  x: number,
  y: number,
  w: number,
  h: number,
): CanvasGradient {
  const [x1, y1] =
    gradient.direction === "horizontal"
      ? [x + w, y]
      : [x + (gradient.direction === "diagonal" ? w : 0), y + h]
  const linear = ctx.createLinearGradient(x, y, x1, y1)
  linear.addColorStop(0, gradient.from)
  linear.addColorStop(1, gradient.to)
  return linear
}

/**
 * Draw the full overlay document onto `ctx`. `scale` maps doc-space px to
 * ctx px (export uses 1 when the canvas matches `doc.canvas`). Pass a
 * pre-solved layout to guarantee identity with what an editor displayed.
 */
export function renderOverlay(
  ctx: CanvasRenderingContext2D,
  doc: OverlayDoc,
  scale: number,
  assets: RenderAssets = {},
  presolved?: SolvedLayout,
): void {
  const layout = presolved ?? solveDoc(doc)

  for (const band of layout.bands) {
    const { background } = band.section
    if (!background) continue
    const x = band.x * scale
    const y = band.y * scale
    const w = band.width * scale
    const h = band.height * scale
    ctx.save()
    ctx.globalAlpha = background.opacity ?? 1
    ctx.fillStyle = background.gradient
      ? gradientFillStyle(ctx, background.gradient, x, y, w, h)
      : background.color
    roundedRectPath(ctx, x, y, w, h, (background.radius ?? 0) * scale)
    ctx.fill()
    ctx.restore()
  }

  for (const box of layout.boxes) {
    if (box.kind === "spacer") continue
    if (box.kind === "divider" && box.item.type === "divider") {
      ctx.save()
      ctx.fillStyle = box.item.color
      roundedRectPath(
        ctx,
        box.x * scale,
        box.y * scale,
        box.width * scale,
        box.height * scale,
        (box.item.radius ?? 0) * scale,
      )
      ctx.fill()
      ctx.restore()
      continue
    }
    if (box.kind === "logo") {
      const logo = assets.logo
      if (!logo) continue
      // Contain-fit inside the box, centered.
      const boxW = box.width * scale
      const boxH = box.height * scale
      const ratio = Math.min(
        boxW / (logo.naturalWidth || 1),
        boxH / (logo.naturalHeight || 1),
      )
      const w = (logo.naturalWidth || 1) * ratio
      const h = (logo.naturalHeight || 1) * ratio
      ctx.drawImage(
        logo,
        box.x * scale + (boxW - w) / 2,
        box.y * scale + (boxH - h) / 2,
        w,
        h,
      )
      continue
    }
    if (box.kind !== "text") continue

    const item = box.item
    ctx.save()
    if (item.background) {
      const fillOpacity = item.background.opacity ?? 1
      roundedRectPath(
        ctx,
        box.x * scale,
        box.y * scale,
        box.width * scale,
        box.height * scale,
        item.background.radius * scale,
      )
      if (fillOpacity > 0) {
        ctx.save()
        ctx.globalAlpha = fillOpacity
        ctx.fillStyle = item.background.color
        ctx.fill()
        ctx.restore()
      }
      if (item.background.borderColor && item.background.borderWidth) {
        ctx.strokeStyle = item.background.borderColor
        ctx.lineWidth = item.background.borderWidth * scale
        ctx.stroke()
      }
    }

    applyTextStyle(ctx, item, scale)
    ctx.fillStyle = item.color
    ctx.textBaseline = "alphabetic"
    ctx.textAlign = "left"
    if (item.shadow) {
      ctx.shadowColor = "rgba(0, 0, 0, 0.55)"
      ctx.shadowBlur = item.fontSize * scale * 0.18
      ctx.shadowOffsetY = item.fontSize * scale * 0.05
    }
    const align = item.textAlign ?? "left"
    // Baseline offset ≈ cap alignment inside the line box; matches the DOM
    // editor's line-height rendering closely enough for WYSIWYG.
    const firstBaseline =
      box.textY * scale +
      (box.lineHeightPx * scale) / 2 +
      item.fontSize * scale * 0.35
    box.lines.forEach((line, i) => {
      const lineWidth = ctx.measureText(line).width
      const lineX =
        align === "center"
          ? box.textX * scale + (box.textWidth * scale - lineWidth) / 2
          : align === "right"
            ? box.textX * scale + box.textWidth * scale - lineWidth
            : box.textX * scale
      const baseline = firstBaseline + i * box.lineHeightPx * scale
      ctx.fillText(line, lineX, baseline)
      if (item.underline) {
        ctx.fillRect(
          lineX,
          baseline + item.fontSize * scale * 0.1,
          lineWidth,
          Math.max(1, item.fontSize * scale * 0.07),
        )
      }
    })
    ctx.restore()
  }
}
